import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainOperation, DomainOperationPrice } from '../types/models/DomainPricing';
import type { ApiResponseDomainOperationPrices } from '../types/api/ApiResponseDomainPricing';

interface UseDomainRegistrationPricesReturn {
  /** The registration operation catalog entry (label/description), or `null` until loaded. */
  operation: DomainOperation | null;
  /** The active, priced registration TLDs for the in-scope client (override-then-default). */
  prices: DomainOperationPrice[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /prices/domains/registration` — the active, priced registration TLDs for the in-scope client
 * (the generic `GET /prices/domains/{slug}` route with `slug = registration`). Used to seed the TLD
 * checkbox list + baseline prices on the search step. A TLD that's active but unpriced for
 * registration is omitted by the backend. JSend: success is `body.status === 'success'` + a `prices`
 * array, never `response.ok`.
 */
export const useDomainRegistrationPrices = (): UseDomainRegistrationPricesReturn => {
  const apiFetch = useApiFetch();
  const auth = useAuth();
  const [operation, setOperation] = useState<DomainOperation | null>(null);
  const [prices, setPrices] = useState<DomainOperationPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/prices/domains/registration');
      const body = (await response.json().catch(() => null)) as ApiResponseDomainOperationPrices | null;

      if (body?.status === 'success' && Array.isArray(body.data?.prices)) {
        setOperation(body.data.operation ?? null);
        setPrices(body.data.prices);
      } else {
        setPrices([]);
        setError(body?.message ?? 'Failed to load registration prices');
      }
    } catch (err) {
      setPrices([]);
      setError(err instanceof Error ? err.message : 'An error occurred while loading registration prices');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchPrices();
    }
  }, [auth.isAuthenticated, fetchPrices]);

  return { operation, prices, loading, error, refetch: fetchPrices };
};
